import React, { useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { gsap } from 'gsap'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin.js'

// Styles
import { MapRouteWrapper, MapRouteArrow } from './index.style'

// SVGs
import MapArrow from '../../svgs/MapArrow'

gsap.registerPlugin(MotionPathPlugin)

const MapRoute = ({
  inView,
  left,
  right,
  top,
  width,
  active,
  route,
  duration,
  delay,
  color,
  children,
}) => {
  const staticArrow = useRef(),
    movingArrow = useRef()

  const staticTimeline = useRef(),
    movingTimeline = useRef()

  const movingDelay = useRef()

  const move = () => {
    if (!movingTimeline.current) return

    clearTimeout(movingDelay.current)
    movingTimeline.current.play(0.0001)
  }

  const reset = () => {
    if (!movingTimeline.current) return

    clearTimeout(movingDelay.current)
    movingTimeline.current.pause()
    movingTimeline.current.seek(0.0001)
  }

  useEffect(() => {
    if (active) {
      movingDelay.current = setTimeout(move, delay * 1000)
    } else {
      reset()
    }
  }, [active, delay])

  const createStaticTimeline = useCallback(() => {
    staticTimeline.current = gsap.to(staticArrow.current, {
      motionPath: {
        path: route.current,
        align: route.current,
        alignOrigin: [0.5, 0.5],
        autoRotate: true,
      },
      transformOrigin: '50% 50%',
      duration: duration,
      paused: true,
    })

    staticTimeline.current.seek(0.0001)
  }, [staticArrow, route, duration])

  const createMovingTimeline = useCallback(() => {
    movingTimeline.current = gsap.timeline({
      paused: true,
    })

    movingTimeline.current.to(
      movingArrow.current,
      {
        motionPath: {
          path: route.current,
          align: route.current,
          alignOrigin: [0.5, 0.5],
          autoRotate: true,
        },
        transformOrigin: '50% 50%',
        duration: duration,
        ease: 'power1.inOut',
      },
      0
    )

    movingTimeline.current.to(
      route.current,
      {
        strokeDashoffset: 0,
        duration: duration,
        ease: 'power1.inOut',
      },
      0
    )

    movingTimeline.current.seek(0.0001)
  }, [movingArrow, route, duration])

  const update = useCallback(() => {
    staticTimeline.current.pause()
    movingTimeline.current.pause()
    staticTimeline.current.seek(0.0001)
    movingTimeline.current.seek(0.0001)
    staticTimeline.current.kill()
    movingTimeline.current.kill()
    createStaticTimeline()
    createMovingTimeline()
  }, [createStaticTimeline, createMovingTimeline])

  useEffect(() => {
    createStaticTimeline()
    createMovingTimeline()
    window.addEventListener('resize', update)

    return () => {
      window.removeEventListener('resize', update)
    }
  }, [update, createStaticTimeline, createMovingTimeline])

  return (
    <MapRouteWrapper
      inView={inView}
      left={left}
      right={right}
      top={top}
      width={width}
      active={active}
    >
      {children}
      <MapRouteArrow ref={staticArrow}>
        <MapArrow circleFill={color} />
      </MapRouteArrow>
      <MapRouteArrow ref={movingArrow}>
        <MapArrow circleFill={color} />
      </MapRouteArrow>
    </MapRouteWrapper>
  )
}

MapRoute.propTypes = {
  inView: PropTypes.bool,
  left: PropTypes.number,
  right: PropTypes.number,
  top: PropTypes.number,
  width: PropTypes.number,
  active: PropTypes.bool,
  route: PropTypes.object,
  duration: PropTypes.number,
  delay: PropTypes.number,
  color: PropTypes.string,
}

MapRoute.defaultProps = {
  delay: 0,
}

export default MapRoute
